"use client";

import { useEffect } from "react";

const DEFAULT_SERVICE_WORKER_URL = "/service-worker.js";
const reloadCurrentPage = () => window.location.reload();
const shouldRegisterServiceWorker = () => process.env.NODE_ENV === "production";

type ServiceWorkerInitProps = {
  reloadPage?: () => void;
  serviceWorkerUrl?: string;
};

export default function ServiceWorkerInit({
  reloadPage = reloadCurrentPage,
  serviceWorkerUrl = DEFAULT_SERVICE_WORKER_URL,
}: ServiceWorkerInitProps) {
  useEffect(() => {
    if (!shouldRegisterServiceWorker() || !("serviceWorker" in navigator)) {
      return;
    }

    const hadController = Boolean(navigator.serviceWorker.controller);
    let refreshing = false;

    const handleControllerChange = () => {
      if (!hadController || refreshing) {
        return;
      }

      refreshing = true;
      reloadPage();
    };

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register(serviceWorkerUrl, {
          scope: "/",
          updateViaCache: "none",
        });

        await registration.update();
      } catch {
        // Service worker registration should never block page rendering.
      }
    };

    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
    void registerServiceWorker();

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, [reloadPage, serviceWorkerUrl]);

  return null;
}
